import { AnswerType, AnswerValueType, getSequenceAnswersAnswerType } from "../types/Answer";
import { Dependency } from "../types/Dependency";
import { QuestionType, QuestionTypeEnum, RuntimeInstance } from "../types/Question";
import { QuestionnaireDetailsType } from "../types/Questionnaire";
import { findElementsByKey } from "../components/organisms/Home/Mountain/landscapeLogic";
import { hasAsweredFunctionsByType, getAnswerValue } from "../helper/answer"; // adjust import

/* ----------------------------- Set engine ----------------------------- */
// Follow-up sets: a checkbox option or a number question can reveal a "set" of
// questions. Members live in `questions` (set_driver_question_id != null) but are
// pulled out of the linear flow and injected as instances when their driver is
// answered. See [[followup-sets]] for the data model.

// Locale-aware ordinal suffixes for the study's supported languages, selected via the
// runtime's Intl.PluralRules (ordinal). Admins never write these and they're never
// translated — the ordinal is a pure function of (number, language). Any language not
// listed falls back to the plain number, so it degrades gracefully and never breaks.
const ORDINAL_SUFFIX: Record<string, Record<string, string>> = {
  en: { one: "st", two: "nd", few: "rd", other: "th" },
  fr: { one: "er", other: "e" },
  // Add more study languages here as needed (e.g. es: { other: "º" }).
};

export const formatOrdinal = (n: number, lang: string): string => {
  const base = (lang || "").split("-")[0].toLowerCase();
  const suffixes = ORDINAL_SUFFIX[base];
  if (!suffixes) return String(n);
  try {
    const category = new Intl.PluralRules(lang, { type: "ordinal" }).select(n);
    return `${n}${suffixes[category] ?? suffixes.other ?? ""}`;
  } catch {
    return String(n);
  }
};

export const formatRepetitionLabel = (
  template: string | null | undefined,
  n: number,
  lang = "en",
): string =>
  (template || "")
    .replace(/\{ordinal\}/gi, formatOrdinal(n, lang))
    .replace(/\{n\}/gi, String(n));

const bySetOrder = (a: QuestionType, b: QuestionType): number => (a.set_order ?? 0) - (b.set_order ?? 0);

export const isSetMember = (q: QuestionType): boolean => q.set_driver_question_id != null;

export const topLevelQuestions = (questions: QuestionType[]): QuestionType[] =>
  questions.filter((q) => !isSetMember(q)).sort((a, b) => a.order - b.order);

const membersOf = (driverId: number, questions: QuestionType[]): QuestionType[] =>
  questions.filter((q) => q.set_driver_question_id === driverId);

const isDriver = (q: QuestionType, questions: QuestionType[]): boolean =>
  membersOf(q.id, questions).length > 0;

const cloneInstance = (q: QuestionType, runtime: RuntimeInstance): QuestionType =>
  ({ ...q, runtime } as QuestionType);

/**
 * Stable key identifying one set-member instance, and the single source of truth for
 * that format (used both when expanding the flow and when restoring saved answers).
 * Number repeats key by iteration index (`i`); checkbox fan-out keys by the option id
 * (`o`) — which is also the answer's instance_index, so each option's answers persist
 * and restore distinctly.
 */
const setInstanceKey = (driver: QuestionType, instanceIndex: number, memberId: number): string =>
  driver.type === QuestionTypeEnum.CHECKBOX
    ? `d${driver.id}:o${instanceIndex}:q${memberId}`
    : `d${driver.id}:i${instanceIndex}:q${memberId}`;

const findAnswer = (
  answers: AnswerType[],
  questionId: number,
  instanceKey: string | null
): AnswerType | undefined =>
  answers.find((a) => a.questionId === questionId && (a.instanceKey ?? null) === instanceKey);

/** Expand a driver question into the ordered instances its current answer reveals. */
export const expandDriver = (
  driver: QuestionType,
  answerValue: AnswerValueType | undefined,
  questions: QuestionType[],
  lang = "en"
): QuestionType[] => {
  const members = membersOf(driver.id, questions);
  if (members.length === 0) return [];

  if (driver.type === QuestionTypeEnum.NUMBER) {
    const repeatMembers = members.filter((m) => m.set_trigger_answer_id == null).sort(bySetOrder);
    const raw = Number(answerValue);
    const n = Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : 0;
    const label = (driver.detail as { repetition_label?: string | null })?.repetition_label;
    const out: QuestionType[] = [];
    // With a single repetition there's nothing to disambiguate, so skip the
    // per-iteration heading ("First MRI" etc.) — show it only when n > 1.
    for (let i = 1; i <= n; i++) {
      repeatMembers.forEach((m, j) => {
        out.push(cloneInstance(m, {
          key: setInstanceKey(driver, i, m.id),
          driverId: driver.id,
          instanceIndex: i,
          triggerAnswerId: null,
          heading: j === 0 && n > 1 ? formatRepetitionLabel(label, i, lang) : null,
        }));
      });
    }
    return out;
  }

  if (driver.type === QuestionTypeEnum.CHECKBOX) {
    const selected = Array.isArray(answerValue)
      ? answerValue
      : answerValue != null ? [answerValue] : [];
    const options = (driver.detail as { answers?: Array<{ id: number; label: string }> })?.answers ?? [];
    const out: QuestionType[] = [];
    options.forEach((opt) => {
      if (!selected.includes(opt.id)) return;
      const optMembers = members.filter((m) => m.set_trigger_answer_id === opt.id).sort(bySetOrder);
      optMembers.forEach((m, j) => {
        out.push(cloneInstance(m, {
          // instanceIndex = option id (not a constant 1) so each selected option's
          // member answers persist under a distinct instance_index instead of colliding.
          key: setInstanceKey(driver, opt.id, m.id),
          driverId: driver.id,
          instanceIndex: opt.id,
          triggerAnswerId: opt.id,
          heading: j === 0 ? opt.label : null,
        }));
      });
    });
    return out;
  }

  return [];
};

/**
 * Expected number of steps in the (instance-expanded) flow, for the progress bar.
 * Set members are pulled out of the linear flow, so the base is the top-level
 * questions; each answered driver then contributes the instances its current answer
 * reveals (N×members for number, one set per selected option for checkbox). The total
 * grows as drivers are answered — matching what the user will actually be shown — so
 * `history.length / total` no longer exceeds 100%.
 */
export const countFlowQuestions = (
  questions: QuestionType[],
  answers: AnswerType[]
): number => {
  const top = topLevelQuestions(questions);
  let total = top.length;
  for (const q of top) {
    if (isDriver(q, questions)) {
      const driverAnswer = findAnswer(answers, q.id, null);
      total += expandDriver(q, driverAnswer?.value, questions).length;
    }
  }
  return total;
};

/**
 * Rebuild the answers for set-member instances after a reload. Saved set answers carry an
 * instance_index (>0); each is mapped back to its runtime instance via setInstanceKey, so
 * the value lands on the right instance instead of showing empty. Top-level answers
 * (instance_index 0) are handled by the normal per-question seeding and skipped here.
 */
export const restoreInstanceAnswers = (
  questions: QuestionType[],
  formattedAnswers: getSequenceAnswersAnswerType[]
): AnswerType[] => {
  const out: AnswerType[] = [];
  for (const ans of formattedAnswers) {
    const idx = Number(ans.instance_index) || 0;
    if (idx <= 0) continue;
    const member = questions.find((q) => q.id === ans.question_id);
    if (!member || member.set_driver_question_id == null) continue;
    const driver = questions.find((q) => q.id === member.set_driver_question_id);
    if (!driver) continue;
    out.push({
      questionId: member.id,
      questionType: member.type,
      value: getAnswerValue(member.type, ans),
      instanceKey: setInstanceKey(driver, idx, member.id),
    });
  }
  return out;
};

/** Where the flow goes after a question/driver: explicit routing, else next top-level. */
const resolveExit = (
  question: QuestionType,
  questions: QuestionType[],
  dependencies: Dependency[],
  answers: AnswerType[]
): QuestionType | null => {
  const dependency = dependencies.find((d) => d.id === question.id);
  const answer = findAnswer(answers, question.id, null);

  let questionDepId: number | undefined;
  if (dependency?.answers && dependency.answers.length > 0) {
    const answerDep = dependency.answers.find((a) => a.id === answer?.value);
    questionDepId = answerDep?.next_question?.id;
    if (answerDep?.endAfter) return null;
  } else if (dependency?.endAfter) {
    return null;
  }

  if (!questionDepId) questionDepId = dependency?.next_question?.id ?? undefined;
  if (questionDepId) return questions.find((q) => q.id === questionDepId) ?? null;

  // Fallthrough: next top-level question by order (set members are skipped).
  const top = topLevelQuestions(questions);
  const idx = top.findIndex((q) => q.id === question.id);
  return idx >= 0 ? top[idx + 1] ?? null : null;
};

/**
 * The next question to show. `current` is the runtime question (it may be a set
 * instance, carrying `current.runtime`). With no sets this reduces to the
 * original linear/skip-logic behavior.
 */
export function getNextQuestion(
  questions: QuestionType[],
  dependencies: Dependency[],
  current: QuestionType,
  answers: AnswerType[],
  lang = "en"
): QuestionType | null {
  // 1. Inside a set: advance to the next member instance, else exit via the driver.
  if (current.runtime) {
    const driver = questions.find((q) => q.id === current.runtime!.driverId);
    if (!driver) return null;
    const driverAnswer = findAnswer(answers, driver.id, null);
    const expansion = expandDriver(driver, driverAnswer?.value, questions, lang);
    const pos = expansion.findIndex((e) => e.runtime!.key === current.runtime!.key);
    if (pos >= 0 && pos < expansion.length - 1) return expansion[pos + 1];
    return resolveExit(driver, questions, dependencies, answers);
  }

  // 2. A driver question: enter its first revealed instance, if any.
  if (isDriver(current, questions)) {
    const driverAnswer = findAnswer(answers, current.id, null);
    const expansion = expandDriver(current, driverAnswer?.value, questions, lang);
    if (expansion.length > 0) return expansion[0];
  }

  // 3. Normal routing (set members skipped in the fallthrough).
  return resolveExit(current, questions, dependencies, answers);
}

export const isMandatoryQuestionnaire = (keywords: string) => {    
  return !findElementsByKey(keywords);
};


const hasLastQuestionBeenAnswered = (
  dependencies: Dependency[],
  sessionAnswers: AnswerType[]
): boolean => {
  if (!dependencies?.length) return false;

  // last by order
  const lastDep = dependencies.reduce<Dependency | null>((acc, d) => {
    if (!acc) return d;
    return (d.order ?? 0) > (acc.order ?? 0) ? d : acc;
  }, null);

  if (!lastDep) return false;

  // ✅ compare by questionId (dependency.id is the question id)
  return sessionAnswers.some(
    (a) =>
      a.questionId === lastDep.id &&
      (hasAsweredFunctionsByType[a.questionType]?.(a.value) ?? false)
  );
};


const hasEndAfterBeenMet = (
  dependencies: Dependency[],
  sessionAnswers: AnswerType[]
): boolean => {
  for (const ans of sessionAnswers) {
    const dep = dependencies.find((d) => d.id === ans.questionId);
    if (!dep) continue;

    const isAnswered = hasAsweredFunctionsByType[ans.questionType]?.(ans.value) ?? false;
    if (!isAnswered) continue;

    if (dep.endAfter) return true;

    if (dep.answers?.length) {
      const selectedIds = (Array.isArray(ans.value) ? ans.value : [ans.value])
        .map((v) => (typeof v === 'string' ? Number(v) : v))
        .filter((v) => v != null && !Number.isNaN(v as number)) as number[];

      if (dep.answers.some((a) => selectedIds.includes(a.id) && a.endAfter)) {
        return true;
      }
    }
  }
  return false;
};



export const isQuestionnaireDone = (
  dependencies: Dependency[],
  sessionAnswers: AnswerType[]
): boolean => {
  if (!dependencies?.length) return false;
  return (
    hasEndAfterBeenMet(dependencies, sessionAnswers) ||
    hasLastQuestionBeenAnswered(dependencies, sessionAnswers)
  );
};

/**
 * Calculate total score of a questionnaire if it is scored
 * @param questions - Questionnaire questions with answers + scores
 * @param sessionAnswers - User's answers
 * @param is_scored - Whether the questionnaire is scored
 */
export function calculateQuestionnaireScore(
  questions: QuestionType[],
  sessionAnswers: AnswerType[],
  is_scored: boolean
): number {
  if (!is_scored) return null;

  let total = 0;

  for (const ans of sessionAnswers) {
    const question = questions.find((q) => q.id === ans.questionId);
    if (!question) continue;

    const coefficient = question.score_coefficient ?? 1;

    // ▼ SELECT-TYPE QUESTIONS ---------------------------------------
    if (["RADIOBUTTON", "CHECKBOX", "DROPDOWN"].includes(question.type)) {
      const detail: any = question.detail;
      if (!detail?.answers) continue;

      // Checkbox returns array, others return single value
      const selectedIds = Array.isArray(ans.value) ? ans.value : [ans.value];

      for (const id of selectedIds) {
        const answerDef = detail.answers.find((a: any) => a.id === id);
        if (answerDef && answerDef.score != null) {
          total += Number(answerDef.score) * coefficient;
        }
      }
    }

    // ▼ SLIDER QUESTIONS --------------------------------------------
    if (question.type === "SLIDER") {
      const value = Number(ans.value) || 0;
      total += value * coefficient;
    }

    if (question.type === "NUMBER") {
      const value = Number(ans.value) || 0;
      total += value * coefficient;
    }
  }

  return total;
}